import { Point } from 'geojson';
import { useRef, useState } from 'react';
import { Business, Offer, OfferDTO, OfferGeolocationDTO } from '../../backend/db/all-entities';
import { PRICING_TIERS } from '../../policy/business-pricing-tiers';
import inputGrid from '../../styles/input-grid.module.css';
import { UserCancelledOperationError } from '../../utils/errors';
import CategoryChooser from '../category/chooser';
import { CoreUIContext } from '../core-ui/provider';
import ImageInput from '../inputs/image-input';
import { useImageUpload } from '../inputs/image-input/helpers';
import LocationPicker from '../inputs/map-location-picker';
import { getKey } from '../inputs/map-location-picker/location-utils';
import NumberInput from '../inputs/number';
import { DialogContainer } from '../core-ui/dialog-container';

/**
 * Shows a dialog allowing a user to edit an offer. If no offer is provided no initial data will be populated
 *
 * @param showDialog The showDialog function from CoreUIContext provider
 * @param pricingPlan The editing business's pricing plan, to determine how many geolocations are allowed
 * @param availableGeolocations The points which are allowed to be selected when editing the offer
 * @param offer The initial offer data
 * @returns The edited version of the offer
 */
export function showOfferEditor(
  showDialog: CoreUIContext['showDialog'],
  pricingPlan: Business['pricingPlan'],
  availableGeolocations: Point[],
  offer?: Offer | Partial<Omit<Offer, 'offerer'>>
): Promise<typeof offer & Partial<Offer>> {
  return new Promise<typeof offer & OfferDTO>((res, rej) => {
    const isNewOffer = offer == null;
    showDialog(function OfferEditor() {
      const formRef = useRef<HTMLFormElement>(null);
      const [name, setName] = useState(offer?.name ?? '');
      const [price, setPrice] = useState(offer?.price);
      const [description, setDescription] = useState(offer?.description ?? '');
      const [selectedCategory, setSelectedCategory] = useState(offer?.category);
      const [geolocations, setGeolocations] = useState<OfferGeolocationDTO[]>(offer?.geolocations ?? []);
      const imageUpload = useImageUpload(offer?.imageUrl);
      return (
        <DialogContainer
          title={isNewOffer ? 'Create Offer' : 'Edit Offer'}
          buttons={['Cancel', ['Save', 'primary']]}
          onClose={button => {
            if (button === 'Save') {
              if (formRef.current?.reportValidity()) {
                const editedOffer: OfferDTO = {
                  ...offer,
                  name,
                  price: price as NonNullable<typeof price>,
                  description,
                  imageUrl: imageUpload.imageUrl,
                  geolocations,
                  category: selectedCategory as NonNullable<typeof selectedCategory>
                };
                res(editedOffer as typeof editedOffer & typeof offer);
              } else return false;
            } else rej(new UserCancelledOperationError());
            return true;
          }}
        >
          <form ref={formRef} onSubmit={e => e.preventDefault()}>
            <div className={`${inputGrid.inputGrid} mx-auto mb-4`}>
              <input placeholder='Offer name' value={name} required onChange={e => setName(e.target.value)} />
              <NumberInput
                max={9999}
                placeholder='Price (USD)'
                required
                initialValue={offer != null ? offer?.price : undefined}
                onSet={setPrice}
              />
              <textarea
                placeholder='Description'
                required
                value={description}
                onChange={e => {
                  setDescription(e.target.value);
                }}
              />
              <ImageInput imageUpload={imageUpload} />
            </div>
            <div className='mb-2'>
              <CategoryChooser
                radioInputName='offer-category-chooser'
                categoryType='offer'
                required
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
              />
            </div>
            <LocationPicker
              locations={geolocations.map(l => l.point)}
              addLocation={point => {
                setGeolocations(locations => [...locations, { point }]);
              }}
              removeLocation={point => {
                const key = getKey(point);
                setGeolocations(locations => locations.filter(l => getKey(l.point) !== key));
              }}
              locationSuperset={availableGeolocations}
              min={1}
              max={PRICING_TIERS[pricingPlan].maxGeolocations}
            />
          </form>
        </DialogContainer>
      );
    });
  });
}
